/*global ViewController, WebView*/

/**
 * Multi document download popup of the document grids (GED)
 *
 * It offers the download format (zip / merged PDF) and, when at least one selected
 * document has a completed signature, the choice between the signed and the raw
 * version of the files. A choice that doesn't apply is not displayed, and when no
 * choice at all applies the selected documents are listed instead (see #5021).
 *
 * Merging into one PDF puts the pages in the order of the list, so that order is the
 * user's to set : on the merged PDF the compact chips give way to the full list, made
 * sortable by drag and drop. The name of the produced file is editable too, both
 * choices the popup took away from doc-merge-pdf-popup when it replaced it.
 *
 * Entered with :
 *  {
 *      docs: [{id, attach_id, mimetype, name, reference}],
 *      filename: "the name of the downloaded zip/pdf, without extension"
 *  }
 */

//number of documents displayed as chips, the others are counted in "+ n others"
const CHIP_LIMIT = 4 ;

const MIME_ICONS = [
    { test: /pdf/, icon: "far fa-file-pdf" },
    { test: /word|opendocument\.text/, icon: "far fa-file-word" },
    { test: /sheet|excel|csv/, icon: "far fa-file-excel" },
    { test: /presentation|powerpoint/, icon: "far fa-file-powerpoint" },
    { test: /^image\//, icon: "far fa-file-image" },
    { test: /zip|compressed|tar/, icon: "far fa-file-archive" },
    { test: /^text\//, icon: "far fa-file-alt" }
] ;

//only what Bootstrap has no utility for : chip truncation, list scrolling and the
//popup width. loadStaticCss is the framework way to ship component CSS
//(GridController, KanbanController do the same) ; a view level .scss is never
//loaded by WebView.
const CSS = [
    ".doc-download-chip-label {",
    "    overflow: hidden;",
    "    text-overflow: ellipsis;",
    "    white-space: nowrap;",
    "    max-width: 12rem;",
    "}",
    ".doc-download-list {",
    "    max-height: 11rem;",
    "    overflow-y: auto;",
    "}",
    //List.js ships ".list-sort-handle { display: none }" and only unhides the handles
    //of a "tr[data-list-sortable]", the line input case. Ours are in a <li>.
    ".doc-download-list .list-sort-handle {",
    "    display: inline-block;",
    "}",
    //the placeholder Sortable leaves behind while a document is being dragged
    ".doc-download-list .sortable-ghost {",
    "    opacity: .4;",
    "}",
    //the "medium" popups are 50% of the viewport, way too wide for two columns of
    //choices on a large screen. .modal-dialog keeps the selector above the
    //framework .modal-md rule whatever the order the two are injected in
    ".modal-dialog.doc-download-dialog {",
    "    max-width: 36rem;",
    "}"
].join("\n") ;

/**
 * Give the FontAwesome icon matching a mimetype
 *
 * @param {string} mimetype the document mimetype
 * @returns {string} the icon classes
 */
function iconOf(mimetype){
    const found = MIME_ICONS.find(entry=>entry.test.test(mimetype||"")) ;
    return found?found.icon:"far fa-file" ;
}

/**
 * Compute everything the template displays from the remaining documents
 *
 * Called on enter and after each document removal, so removing the document that
 * prevented merging displays the format cards, and removing the only signed
 * document hides the signature choice.
 *
 * @param {object} data the view data
 */
function computeState(data){
    data.count = data.docs.length ;
    data.canMerge = data.docs.length > 0 && data.docs.every(doc=>doc.mimetype === "application/pdf") ;
    data.hasSign = data.docs.some(doc=>!!doc.sign_id) ;
    data.hasOption = data.canMerge || data.hasSign ;

    if(!data.canMerge){
        //only one format is possible, there is no choice to make
        data.format = "zip" ;
    }else if(!data.formatChosen){
        data.format = "pdf" ;
    }
    if(!data.hasSign){
        data.signature = "without" ;
    }

    //the merge order is only worth setting on a merged PDF ; a zip keeps the files
    //apart. The chips are compact but capped, and one cannot order what one doesn't
    //see, so the ordered case shows the full list instead.
    data.sortable = data.canMerge && data.format === "pdf" ;
    data.showList = !data.hasOption || data.sortable ;
    data.showChips = data.hasOption && !data.showList ;
    //a data-show-if inside a list line reads the line data, not the view data
    data.docs.forEach(doc=>{ doc.sortable = data.sortable ; }) ;
    data.extension = data.format === "zip"?".zip":".pdf" ;

    data.chips = data.showChips?data.docs.slice(0, CHIP_LIMIT):[] ;
    data.remaining = Math.max(0, data.docs.length - CHIP_LIMIT) ;
    //count is the i18next option driving the _plural variant of the key
    data.subtitle = WebView.tr("doc.downloadSubtitle", {count: data.count}) ;
    data.remainingLabel = WebView.tr("doc.downloadMore", {count: data.remaining}) ;
}

/**
 * Read the completed signatures of the given documents
 *
 * The grid rows don't carry the signature, and a signature completed through
 * updateStatus is flagged completed without any signed PDF being generated, so
 * only iz_sign can tell whether the signed choice must be offered.
 *
 * @param {object} api the service client
 * @param {Array<object>} docs the documents to look at
 * @returns {Promise<object>} map of document id to sign id
 */
function readCompletedSigns(api, docs){
    return new Promise((resolve, reject)=>{
        api.db.multiread({
            iz_sign: { search: {
                res_model: "iz.doc",
                res_id: docs.map(doc=>doc.id),
                state: "completed"
            } }
        }, (err, reads)=>{
            if(err){ return reject(err) ; }
            const signIdByDocId = {} ;
            for(const sign of (reads.iz_sign||[])){
                if(!signIdByDocId[sign.res_id]){
                    signIdByDocId[sign.res_id] = sign.id ;
                }
            }
            resolve(signIdByDocId) ;
        }) ;
    }) ;
}

/**
 * Give a name the download can be asked for
 *
 * BinaryStorageClient hands the name to the server through the query string, and only
 * drops a part of what would break it, so a name typed by a user is cleaned here :
 * everything a file name has no business holding goes, and an empty result gives the
 * caller the chance to fall back on the default.
 *
 * @param {string} filename the name typed by the user
 * @returns {string} the name to download with, possibly empty
 */
function sanitizeFilename(filename){
    // eslint-disable-next-line no-control-regex
    return (filename||"").replace(/[\\/:*?"<>|&#%'\u0000-\u001f]/g, " ").replace(/\s+/g, " ").trim() ;
}

const docDownloadPopupController = new ViewController(
    {
        directory: "views/attach",
        name: "docDownloadPopup",
        route: "doc-download-popup",
        buttonsHTML: "<!-- no button -->",
        openInPopup: true,
        noTitle: true,
        popup: {
            //the popup draws its own header (title + selection count + close cross)
            noHeader: true,
            size: "medium",
            dialogClass: "doc-download-dialog",
            aboveAll: true
        }
    }
) ;

docDownloadPopupController.prepareDataOnEnter = async function(data){
    this.view.loadStaticCss(CSS) ;

    const docs = ((data&&data.docs)||[]).map(doc=>({
        id: doc.id,
        attach_id: doc.attach_id,
        mimetype: doc.mimetype,
        name: doc.name||doc.reference,
        //two documents can share the same name, keep the reference at hand
        //(empty when the reference is already displayed as the name)
        refLabel: doc.name&&doc.reference?doc.reference:"",
        tooltip: doc.name&&doc.reference?`${doc.reference} - ${doc.name}`:(doc.reference||doc.name),
        icon: iconOf(doc.mimetype)
    })) ;

    const defaultFilename = (data&&data.filename)||WebView.tr("fields.table.iz_doc") ;
    const viewData = {
        docs: docs,
        filename: defaultFilename,
        //what an emptied input falls back to
        defaultFilename: defaultFilename,
        format: "pdf",
        signature: "without",
        formatChosen: false
    } ;

    //without the sign module no document can be signed, don't read iz_sign
    if(docs.length > 0 && this.api.currentUser.rights.group_iz_sign_module_user){
        const signIdByDocId = await readCompletedSigns(this.api, docs) ;
        for(const doc of docs){
            if(signIdByDocId[doc.id]){
                doc.sign_id = signIdByDocId[doc.id] ;
            }
        }
    }

    computeState(viewData) ;
    return viewData ;
} ;

//List.js only creates the Sortable of a data-list-sortable line when the list is
//turned auto, which a plain ViewController has to ask for itself. The list <ul> is a
//static node of the template (it is shown and hidden through a class, not through a
//data-show-if that would replace it), so one call holds as long as that node lives.
//
//setListAuto is not idempotent on the view it is called on : it neither remembers nor
//short circuits, so a second call on the same <ul> would leave two Sortable instances
//listening and reorder the list twice on a single drop. The controller and its view
//are singletons reused at each opening, hence the guard on the node itself.
docDownloadPopupController.onDisplayed = function(){
    const list = this.view.container.querySelector(".doc-download-list") ;
    if(!list || this._sortableList === list){ return ; }
    this._sortableList = list ;
    this.view.setListAuto(true) ;
} ;

docDownloadPopupController.onFormatZip = function(){
    this.data.formatChosen = true ;
    this.data.format = "zip" ;
    this._render() ;
} ;

docDownloadPopupController.onFormatPdf = function(){
    this.data.formatChosen = true ;
    this.data.format = "pdf" ;
    this._render() ;
} ;

docDownloadPopupController.onSignWith = function(){
    this.data.signature = "with" ;
    this._render() ;
} ;

docDownloadPopupController.onSignWithout = function(){
    this.data.signature = "without" ;
    this._render() ;
} ;

docDownloadPopupController.onRemoveChip = function(ev){
    this._removeDoc(ev.data.data) ;
} ;

docDownloadPopupController.onRemoveRow = function(ev){
    this._removeDoc(ev.data.data) ;
} ;

docDownloadPopupController.onCancel = function(){
    this.view.close() ;
} ;

docDownloadPopupController.onCloseCross = function(){
    this.view.close() ;
} ;

docDownloadPopupController.onDownload = function(){
    if(this.data.docs.length === 0){ return ; }

    this._readFilename() ;

    const format = this.data.format ;
    const signature = this.data.signature ;
    const filename = (sanitizeFilename(this.data.filename)||this.data.defaultFilename)+this.data.extension ;

    this.view.longTask(async (done)=>{
        try{
            const attachIds = await this._resolveAttachIds() ;
            const downloadApi = format==="zip"?this.api.readManyZip:this.api.readManyPdf ;
            await new Promise((resolve, reject)=>{
                downloadApi.download(attachIds, filename, err=>err?reject(err):resolve()) ;
            }) ;
            done() ;
            WebView.toast(WebView.tr("doc.downloadStarted", {
                format: WebView.tr(format==="zip"?"doc.asZip":"doc.asPdf"),
                signature: WebView.tr(signature==="with"?"doc.downloadWithSign":"doc.downloadWithoutSign")
            })) ;
            this.view.close() ;
        }catch(err){
            done(err) ;
        }
    }) ;
} ;

/**
 * Give the attachment id to download for each document
 *
 * With signatures, the signed PDF is generated server side and its attachment
 * replaces the raw file. Documents without signature keep their own attachment.
 *
 * @returns {Promise<Array<number>>} the attachment ids, in document order
 */
docDownloadPopupController._resolveAttachIds = async function(){
    const docs = this.data.docs ;
    if(this.data.signature !== "with"){
        return docs.map(doc=>doc.attach_id) ;
    }

    const signIds = docs.filter(doc=>!!doc.sign_id).map(doc=>doc.sign_id) ;
    if(signIds.length === 0){
        return docs.map(doc=>doc.attach_id) ;
    }

    const attachBySignId = await new Promise((resolve, reject)=>{
        this.api.sign.getSignedAttachIds(signIds, (err, result)=>{
            if(err){ return reject(err) ; }
            resolve(result||{}) ;
        }) ;
    }) ;

    return docs.map(doc=>{
        const signedAttachId = doc.sign_id?attachBySignId[doc.sign_id]:null ;
        return signedAttachId||doc.attach_id ;
    }) ;
} ;

/**
 * Remove a document from the download, closing the popup when none is left
 *
 * @param {object} doc the document to remove
 */
docDownloadPopupController._removeDoc = function(doc){
    if(!doc){ return ; }

    this.data.docs = this.data.docs.filter(d=>d.id !== doc.id) ;
    if(this.data.docs.length === 0){
        //nothing left to download
        return this.view.close() ;
    }
    this._render() ;
} ;

/**
 * Take back the name being typed before anything re-renders the input
 *
 * Every card of the popup re-renders the whole view, which would put the bound
 * data.filename back into the input and drop what the user had typed. data-autoupdate
 * is not an option : it re-renders the view on each keystroke (WebView.js).
 */
docDownloadPopupController._readFilename = function(){
    const el = this.view.EL.filename ;
    if(!el){ return ; }
    //kept as typed, empty included : the default only steps in on download, emptying
    //the input mid-edit must not refill it under the user's fingers
    this.data.filename = el.value||"" ;
} ;

docDownloadPopupController._render = function(){
    this._readFilename() ;
    computeState(this.data) ;
    this.view.render(this.data) ;
} ;

module.exports = docDownloadPopupController ;
